import { useState, useCallback } from 'react'
import { POST } from '../../../../services/api'
import {
  ProductImage,
  SkuLocalVariation,
  ProductForSearch,
  CombinedImageItem,
  AnalyzeRecolorRequest,
  AnalyzeRecolorResponse,
  SaveRecolorRequest,
  SaveRecolorResponse,
} from '../types'

export const useProductImageRecolor = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ========================================================================
  // BUSCAR PRODUTOS POR CÓDIGO/NOME
  // ========================================================================
  const searchProducts = useCallback(
    async (searchTerm: string, page: number = 1, take: number = 25): Promise<ProductForSearch[]> => {
      try {
        setLoading(true)
        setError(null)

        if (!searchTerm.trim()) {
          return []
        }

        // POST /product/filter_admin?page=X&take=Y&search=TERMO + body vazio
        const response = await POST(
          `/product/filter_admin?page=${page}&take=${take}&search=${searchTerm.trim()}`,
          {} // Body vazio (opcional do filtro)
        )

        if (response.data && Array.isArray(response.data)) {
          return response.data.map((product: any) => ({
            id: product.id,
            code: product.code,
            name: product.name,
            status: product.status,
            classification: product.classification,
          }))
        }

        return []
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar produtos'
        setError(errorMsg)
        console.error('[searchProducts] Error:', err)
        return []
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // ========================================================================
  // CARREGAR IMAGENS DO PRODUTO (productImages)
  // ========================================================================
  const loadProductImages = useCallback(
    async (productId: number): Promise<ProductImage[]> => {
      try {
        setLoading(true)
        setError(null)

        // ROTA: POST /productimage/filter
        const response = await POST('/productimage/filter', {
          productId,
        })

        if (response.data && Array.isArray(response.data)) {
          return response.data
        }

        return []
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar imagens do produto'
        setError(errorMsg)
        console.error('[loadProductImages] Error:', err)
        return []
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // ========================================================================
  // CARREGAR VARIAÇÕES SKU DO PRODUTO
  // ========================================================================
  const loadProductVariations = useCallback(
    async (productId: number, referenceCode?: string): Promise<SkuLocalVariation[]> => {
      try {
        setLoading(true)
        setError(null)

        // ROTA: POST /variations/filter
        const response = await POST('/variations/filter', {
          productId,
          ...(referenceCode && { referenceCode }),
          status: true, // Filtrar apenas variações ativas
        })

        if (response.data && Array.isArray(response.data)) {
          // Filtra apenas as que estão ativas (status === true)
          return response.data.filter((variation: SkuLocalVariation) => variation.status === true)
        }

        return []
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar variações'
        setError(errorMsg)
        console.error('[loadProductVariations] Error:', err)
        return []
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // ========================================================================
  // COMBINAR IMAGENS DE PRODUTO + VARIAÇÕES (APENAS VÁLIDAS)
  // ========================================================================
  const combineProductImages = useCallback(
    (productId: number, productImages: ProductImage[], skuVariations: SkuLocalVariation[]): CombinedImageItem[] => {
      const combined: CombinedImageItem[] = []

      // Adicionar imagens de produto (apenas as com URL válida)
      productImages.forEach((img, index) => {
        // Validar se path e imgsuffix existem e não são vazios
        if (img.path && img.imgsuffix && img.path.trim() && img.imgsuffix.trim()) {
          const imageUrl = img.path + '/' + img.imgsuffix
          // Usar typeImg se disponível, caso contrário usar índice
          const typeLabel = img.typeImg && img.typeImg.trim() ? img.typeImg : `Image ${index + 1}`

          combined.push({
            id: `product-${img.id}`,
            productId,
            type: 'product-image',
            imageUrl,
            label: `Product Image (${typeLabel})`,
            sourceData: img,
          })
        }
      })

      // Adicionar imagens de variação SKU (apenas as com URL válida)
      skuVariations.forEach((variation) => {
        // Validar se img existe, não é vazio e é uma URL válida
        if (variation.img && variation.img.trim() && variation.img.startsWith('http')) {
          combined.push({
            id: `variation-${variation.id}`,
            productId,
            type: 'sku-variation',
            imageUrl: variation.img, // Já é URL COMPLETA
            label: `SKU Variation (${variation.sku})`,
            sku: variation.sku,
            sourceData: variation,
          })
        }
      })

      return combined
    },
    []
  )

  // ========================================================================
  // ANÁLISE DE RECOLOR (Backend)
  // ========================================================================
  const analyzeRecolor = useCallback(
    async (request: AnalyzeRecolorRequest): Promise<AnalyzeRecolorResponse['data'] | null> => {
      try {
        setLoading(true)
        setError(null)

        // ROTA: POST /recolor/analyze (Backend RecolorController)
        const response = await POST('/recolor/analyze', {
          imageUrl: request.imageUrl,
          objectDescription: request.objectDescription,
          targetColor: request.targetColor,
        })

        if (response.success && response.data) {
          return response.data
        }

        throw new Error(response.error || 'Erro ao analisar recolor')
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao analisar recolor'
        setError(errorMsg)
        console.error('[analyzeRecolor] Error:', err)
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  // ========================================================================
  // VALIDAR TAMANHO DA IMAGEM
  // ========================================================================
  const validateImageSize = useCallback(
    async (imageUrl: string, maxSizeMB: number = 50): Promise<{ size: number; isValid: boolean }> => {
      try {
        const maxBytes = maxSizeMB * 1024 * 1024;
        
        // Tentar obter tamanho via HEAD request
        try {
          const response = await fetch(imageUrl, { method: 'HEAD' });
          const contentLength = response.headers.get('content-length');
          
          if (contentLength) {
            const sizeBytes = parseInt(contentLength, 10);
            const isValid = sizeBytes <= maxBytes;
            
            console.log(
              `[validateImageSize] Size: ${(sizeBytes / 1024 / 1024).toFixed(2)}MB / Limit: ${maxSizeMB}MB → ${isValid ? '✅' : '❌'}`
            );
            
            return { size: sizeBytes, isValid };
          }
        } catch (headErr) {
          console.warn('[validateImageSize] HEAD request failed, will try GET');
        }

        // Fallback: fazer GET e verificar tamanho durante download
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const isValid = blob.size <= maxBytes;

        console.log(
          `[validateImageSize] Size: ${(blob.size / 1024 / 1024).toFixed(2)}MB / Limit: ${maxSizeMB}MB → ${isValid ? '✅' : '❌'}`
        );

        return { size: blob.size, isValid };
      } catch (err) {
        console.error('[validateImageSize] Error:', err);
        throw new Error('Impossível validar tamanho da imagem');
      }
    },
    []
  );

  // ========================================================================
  // APLICAR TRANSFORMAÇÃO DE COR NA IMAGEM (Backend via Jimp)
  // ========================================================================
  const applyRecolorTransformation = useCallback(
    async (
      imageUrl: string,
      targetColor: string,
      bounds: { x: number; y: number; width: number; height: number }
    ): Promise<string | null> => {
      try {
        setError(null)
        console.log(
          `[applyRecolorTransformation] Calling backend: ${imageUrl} to ${targetColor} in bounds`,
        );

        // ✅ CHAMAR BACKEND PARA APLICAR TRANSFORMAÇÃO (sem CORS)
        // Backend faz o download da imagem e aplica transformação com Jimp
        const response = await POST('/recolor/apply-transformation', {
          imageUrl,
          targetColor,
          bounds,
          imageId: undefined,
          appliedColor: undefined,
        });

        // Validar resposta
        if (!response.data || !response.data.data) {
          throw new Error('Resposta inválida do servidor');
        }

        const dataUrl = response.data.data; // Base64 data URL

        console.log(`[applyRecolorTransformation] ✅ Transformação concluída`);

        return dataUrl;
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao transformar cor';
        setError(errorMsg);
        console.error('[applyRecolorTransformation] Error:', err);
        return null;
      }
    },
    [],
  );

  // ========================================================================
  // SALVAR IMAGEM RECOLORIDA (Backend)
  // ========================================================================
  const saveRecolor = useCallback(
    async (request: SaveRecolorRequest): Promise<SaveRecolorResponse['data'] | null> => {
      try {
        setLoading(true)
        setError(null)

        // ROTA: POST /recolor/save (Backend RecolorController)
        const response = await POST('/recolor/save', {
          imageBase64: request.imageBase64,
          subdomain: request.subdomain,
          typefolder: request.typefolder,
          typeimage: request.typeimage,
          ...(request.imageId && { imageId: request.imageId }),
          ...(request.appliedColor && { appliedColor: request.appliedColor }),
        })

        if (response.success && response.data) {
          return response.data
        }

        throw new Error(response.error || 'Erro ao salvar recolor')
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro ao salvar recolor'
        setError(errorMsg)
        console.error('[saveRecolor] Error:', err)
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  return {
    loading,
    error,
    searchProducts,
    loadProductImages,
    loadProductVariations,
    combineProductImages,
    analyzeRecolor,
    validateImageSize,
    applyRecolorTransformation,
    saveRecolor,
  }
}
